import dayjs, { Dayjs } from "dayjs";
import { API } from "../Client";
import { AlpacaTradingDay } from "./types";

// TTL caches for market status calls
let _marketOpenCache: { value: boolean; expiry: number } | null = null;
let _holidayCache: { value: boolean; expiry: number } | null = null;
const MARKET_OPEN_TTL = 60 * 1000; // 1 minute
const HOLIDAY_TTL = 60 * 60 * 1000; // 1 hour

namespace MarketService {
  /**
   * Returns whether market is open (cached for 1 minute)
   *
   * @returns true or false
   */
  export async function isMarketOpen(): Promise<boolean> {
    if (_marketOpenCache && Date.now() < _marketOpenCache.expiry) {
      return _marketOpenCache.value;
    }
    const res = await API.post('/market/open');
    const value = res?.data;
    _marketOpenCache = { value, expiry: Date.now() + MARKET_OPEN_TTL };
    return value;
  }

  /**
   * Fetches last market date
   *
   * @param numDaysAgo number of days ago to start from
   * @returns last market date (ISO String)
   */
  export async function getLastMarketDate(numDaysAgo: number = 0): Promise<string> {
    const res = await API.get('/market/last-market-date', { params: { numDaysAgo } });
    return res?.data;
  }

  /**
   * Returns whether current day is a holiday (cached for 1 hour)
   *
   * @returns true or false
   */
  export async function isHoliday(): Promise<boolean> {
    if (_holidayCache && Date.now() < _holidayCache.expiry) {
      return _holidayCache.value;
    }
    const res = await API.get('/market/holiday');
    const value = res?.data;
    _holidayCache = { value, expiry: Date.now() + HOLIDAY_TTL };
    return value;
  }

   /**
   * Get all trading days between two dates (inclusive)
   * @param start date as Moment or Date type
   * @param end date as Moment or Date type
   * @returns all trading days
   */
   export async function getTradingDays(
    start: Date | Dayjs,
    end: Date | Dayjs,
  ): Promise<AlpacaTradingDay[]> {
    const startDate = dayjs(start).format('YYYY-MM-DD');
    const endDate = dayjs(end).format('YYYY-MM-DD');

    try {
      const res = await API.get('market/trading-days', {
        params: {
          start: startDate,
          end: endDate,
        },
      });

      return res?.data || [];
    } catch (e) {
      console.error(`Failed to retrieve trading days from ${startDate} to ${endDate}`, e);
      return [];
    }
  }

  /**
   * Get latest deposit date (3 trading days from now), and in the event of failure, 3 standard business days later
   * @param format Moment date format string
   * @returns date string
   */
  export async function getLatestDepositDate(format: string = 'MMM, D, YYYY'): Promise<string> {
    // Assuming the market is never closed for more than 6 days (safe arbitrary number because stock market never goes down for more than 3 days)
    const days = await getTradingDays(dayjs(), dayjs().add(6, 'days'));

    if (days.length > 3) {
      // Skip first day (today)
      return dayjs(days[3].date, 'YYYY-MM-DD').format(format);
    }

    // Failsafe
    // TODO: make sure to take into account holidays here later
    return dayjs().add(3, 'days').format(format);
  }

}

export default MarketService;
